import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import OrgOnboardingStep1 from "./OrgOnboardingStep1";
import OrgOnboardingStep2 from "./OrgOnboardingStep2";
import OrgOnboardingStep3 from "./OrgOnboardingStep3";
import OrgOnboardingStep4 from "./OrgOnboardingStep4";
import OrgOnboardingStep5 from "./OrgOnboardingStep5";
import OrgOnboardingStep6 from "./OrgOnboardingStep6";
import OrgOnboardingCompletion from "./OrgOnboardingCompletion";
import { saveOrgProfilePatch } from "@/lib/orgProfilePatch";
import { leadGenerationPatch, resolveLeadGenerationAnswer } from "@/lib/orgLeadGeneration";
import { saveOrgOnboardingBookmark } from "@/lib/orgOnboardingBookmark";
import { changedKeys, pick, same } from "@/lib/orgWizardPatch";
import { parseOfficeString } from "@/lib/orgOffices";
import { officeLocationsFromOrg } from "@/lib/orgOfficeHydration";
import { scanWebsiteIntoDraft } from "@/lib/orgWebsiteScan";
import { useOrgProfileStrength } from "@/hooks/useOrgProfileStrength";
import type { OrgProfileStrength } from "@/lib/orgProfileStrength";
import { FEATURE_FLAGS } from "@/config/featureFlags";

interface OrgOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  // The org row (from useUserOrg via the gate); carries profile_data,
  // is_published, slug, etc. since useUserOrg selects "*".
  org: any | null;
  /** Refetches the shared org row so completeness surfaces reflect saved edits. */
  refetchOrg: () => void | Promise<unknown>;
  /** Ink admin workspace: the dialog is portaled outside the shell's canvas scope, so it carries the scope itself. */
  ink?: boolean;
}

const emptyData = {
  // Step 2 — brand & basics
  logo: "",
  tagline: "",
  description: "",
  websiteUrl: "",
  type: "",
  // On by default: scan the website on leaving step 2 and fill blanks in draft.
  scanWebsite: true,
  // Step 3 — value proposition
  missionStatement: "",
  whatMakesUsDifferent: "",
  // Step 4 — what you offer (no self-ratings: organizations never rate themselves, #606)
  compensationModels: [] as string[],
  providesLeads: false,
  trainingFrequency: [] as string[],
  selectedBenefits: [] as string[],
  // Step 5 — markets & services
  serviceTags: [] as string[],
  marketsServed: [] as string[],
  locations: [] as string[],
};

// Prefill from the existing org so the wizard enriches rather than blanks an
// already-partially-filled profile, and so re-running it never clobbers fields.
const buildInitialData = (org: any) => {
  const pd = (org?.profile_data ?? {}) as any;
  const tf = pd.trainingFrequency;
  const trainingFrequency = Array.isArray(tf)
    ? tf.filter((v: any) => typeof v === "string" && v.trim() !== "")
    : typeof tf === "string"
    ? tf.split(",").map((s: string) => s.trim()).filter(Boolean)
    : [];
  return {
    logo: org?.logo_url || pd.logo || "",
    tagline: pd.tagline || "",
    description: org?.description || pd.description || "",
    websiteUrl: org?.website || pd.websiteUrl || "",
    type: typeof org?.type === "string" ? org.type : "",
    missionStatement: pd.missionStatement || "",
    whatMakesUsDifferent: pd.whatMakesUsDifferent || "",
    compensationModels: Array.isArray(pd.compensationModels) ? pd.compensationModels : [],
    providesLeads: resolveLeadGenerationAnswer(pd) === "provided",
    trainingFrequency,
    selectedBenefits: Array.isArray(pd.selectedBenefits) ? pd.selectedBenefits : [],
    serviceTags: Array.isArray(pd.serviceTags)
      ? pd.serviceTags
      : Array.isArray(org?.service_tags)
      ? org.service_tags
      : [],
    marketsServed: Array.isArray(pd.marketsServed) ? pd.marketsServed : [],
    // office_locations is the authoritative mirror of org_offices. Falling
    // back to legacy profile_data.locations when the mirror is empty would
    // resurrect intentionally deleted offices on the next wizard save.
    locations: officeLocationsFromOrg(org),
  };
};

// Internal step numbering: 1 welcome, 2 brand, 3 value, 4 culture, 5 markets,
// 6 invite, 7 completion. Six content steps, then a completion screen.
const TOTAL_STEPS = 6;

/** The pinned header's title and one-line descriptor for each content step. */
const STEP_HEADERS: Record<number, { title: string; subtitle: string }> = {
  1: {
    title: "Let's tell your organization's story",
    subtitle:
      "This takes about 2–3 minutes. It's what agents see when they discover you on Compaire — and it powers how we match you with the right agents.",
  },
  2: { title: "Brand & basics", subtitle: "The essentials agents see first" },
  3: { title: "Your value proposition", subtitle: "Why agents should choose you" },
  4: { title: "What you offer", subtitle: "This is what makes agent matching work" },
  5: { title: "Markets & services", subtitle: "Where you work and what you specialize in" },
  6: { title: "Invite your team", subtitle: "Optional — you can always do this later" },
};

/** The profile keys the final save may write, in the order the wizard collects them. */
const PERSIST_KEYS = [
  "logo", "tagline", "description", "websiteUrl", "type",
  "missionStatement", "whatMakesUsDifferent",
  "compensationModels", "providesLeads", "trainingFrequency", "selectedBenefits",
  "serviceTags", "marketsServed",
] as const;
/** The brand-step keys leaveBrandStep flushes before the website scan. */
const BRAND_KEYS = ["logo", "tagline", "description", "websiteUrl", "type"] as const;

const OrgOnboardingModal = ({ isOpen, onClose, onComplete, org, refetchOrg, ink = false }: OrgOnboardingModalProps) => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(emptyData);
  // What the wizard was seeded with, so a save can send only what moved since.
  // Advanced on every successful write, so a re-run of a step sends the newer
  // delta rather than re-sending what it already wrote.
  const seedRef = useRef(emptyData);
  const [isSaving, setIsSaving] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedLocal, setPublishedLocal] = useState(!!org?.is_published);
  // The completion screen reads the score the final save produced, so it can
  // celebrate + offer Publish only when the profile actually clears the publish
  // threshold, and otherwise guide the admin to finish in the editor.
  const { refresh: refreshStrength } = useOrgProfileStrength();
  const [completionStrength, setCompletionStrength] = useState<OrgProfileStrength | null>(null);

  // Resume at the furthest step reached, with fresh org-prefilled data, the
  // moment the modal opens — the adjust-state-during-render-on-prop-change
  // pattern, mirroring OnboardingModal, so the first step never flashes stale
  // data. The resume bookmark keeps the wizard re-openable where it left off.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      const pd = (org?.profile_data ?? {}) as any;
      const resumeAt = Number(pd.onboardingStep);
      setCurrentStep(
        Number.isInteger(resumeAt) && resumeAt >= 1 && resumeAt <= TOTAL_STEPS ? resumeAt : 1
      );
      const initial = { ...emptyData, ...buildInitialData(org) };
      setData(initial);
      seedRef.current = initial;
      setPublishedLocal(!!org?.is_published);
      setScanned(false);
    }
  }

  const progress = currentStep <= TOTAL_STEPS ? (currentStep / TOTAL_STEPS) * 100 : 100;

  /** Record the furthest step reached so a reopened wizard resumes here. */
  const markStep = (step: number) => {
    if (!org?.id || step > TOTAL_STEPS) return;
    // Fire and forget: this is a bookmark, not the admin's data. It goes through
    // the bookmark writer, which leaves updated_at and profile_updated_by alone
    // so an editor open on this org is not forced to rebase (#787). It resolves
    // a `failed` result rather than rejecting, so report on the status.
    void saveOrgOnboardingBookmark(org.id, { onboardingStep: step }).then((res) => {
      if (res.status !== "saved") {
        Sentry.captureException(new Error("[org-wizard] mark-step save failed"), {
          tags: { component: "org-onboarding-modal", stage: "mark-step" },
        });
      }
    });
  };

  // Persist through the canonical writer, so the wizard, the editor completeness
  // card, the feed card and the lifecycle emails all score the exact same org
  // row. saveOrgProfilePatch is the RPC wrapper; the merge, the indexed-column
  // dual-writes, the serviceTypes mirror, the legacy-compensation strip and the
  // office replace all happen server-side in one transaction.
  //
  // It sends ONLY the keys that moved since the wizard was seeded, because the
  // seeded copy of an untouched key can be older than an edit the profile editor
  // flushed at wizard-open and the wizard passes no expected version to catch
  // that. A key the admin did not touch is therefore never written at all —
  // offices included, whose replace would otherwise churn every org_offices row
  // on every completion.
  const persist = async (): Promise<boolean> => {
    if (!org?.id) return false;
    const changed = changedKeys(seedRef.current, data, PERSIST_KEYS);
    const locationsChanged = !same(seedRef.current.locations, data.locations);
    const patch: Record<string, unknown> = pick(data, changed);
    // `type` is an org_type enum column; an empty selection is not a value, so
    // drop the key rather than writing "".
    if ("type" in patch && !data.type) delete patch.type;
    // The wizard's leads switch has no "no" position distinct from untouched,
    // so on records "provided" and off clears the answer back to unanswered —
    // never a "no" (OD-1). The tri-state and its mirror move together.
    if ("providesLeads" in patch) {
      delete patch.providesLeads;
      Object.assign(patch, leadGenerationPatch(data.providesLeads ? "provided" : null));
    }

    if (Object.keys(patch).length === 0 && !locationsChanged) {
      // Nothing moved: no RPC at all. The completion flow is unchanged.
      void refetchOrg();
      toast.success("Story saved");
      return true;
    }

    setIsSaving(true);
    try {
      const res = await saveOrgProfilePatch(
        org.id,
        patch,
        // Offices land in the same transaction as the story, and only when the
        // locations actually changed. No expected version: the wizard's patch is
        // narrow and it has no UI to resolve a conflict; the server merge means
        // it can only ever change the keys it sends.
        { offices: locationsChanged ? (data.locations ?? []).map(parseOfficeString) : undefined },
      );
      if (res.status !== "saved") {
        Sentry.captureMessage("[org-wizard] save refused", {
          level: "warning",
          tags: { component: "org-onboarding-modal", stage: "persist" },
          extra: {
            status: res.status,
            message: res.status === "failed" ? res.message : undefined,
            keys: Object.keys(patch),
          },
        });
        toast.error("Could not save your story. Please try again.");
        return false;
      }
      // Advance the seed so re-running the final step sends only the newer delta.
      seedRef.current = { ...seedRef.current, ...pick(data, changed), locations: data.locations };
      // Refresh the shared org row so the surfaces behind the modal reflect the
      // save; not awaited past the boolean the caller needs.
      void refetchOrg();
      toast.success("Story saved");
      return true;
    } catch (err: any) {
      console.error("Failed to save org onboarding data:", err);
      Sentry.captureException(err, {
        tags: { component: "org-onboarding-modal", stage: "persist" },
      });
      toast.error(err?.message || "Could not save your story. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const advance = (to: number) => {
    setCurrentStep(to);
    markStep(to);
  };

  // Leaving step 2 (brand & basics): save what they typed, then — if the "scan
  // my website" toggle is on and a website is present — scrape the site and
  // auto-fill blank fields into the draft, so the later steps arrive pre-filled
  // for review. The scan runs at most once per wizard open; nothing here blocks
  // advancing on a scan that finds nothing or fails.
  const leaveBrandStep = async () => {
    if (org?.id) {
      // Same delta rule as persist: flush only what the admin changed on this
      // step, so an untouched brand field never overwrites a newer editor edit.
      const changed = changedKeys(seedRef.current, data, BRAND_KEYS);
      const patch: Record<string, unknown> = pick(data, changed);
      if ("type" in patch && !data.type) delete patch.type;
      if (Object.keys(patch).length > 0) {
        const res = await saveOrgProfilePatch(org.id, patch);
        if (res.status === "saved") {
          seedRef.current = { ...seedRef.current, ...pick(data, changed) };
        } else {
          Sentry.captureMessage("[org-wizard] save refused", {
            level: "warning",
            tags: { component: "org-onboarding-modal", stage: "brand-step" },
            extra: {
              status: res.status,
              message: res.status === "failed" ? res.message : undefined,
              keys: Object.keys(patch),
            },
          });
        }
        // A refused flush does not block advancing — the final persist re-sends
        // any key that is still changed.
      }

      if (data.scanWebsite && !scanned && data.websiteUrl.trim() !== "") {
        // Staff may already have prepared cards for this org (Console
        // build-out). Never let the wizard's single-page auto-apply overwrite a
        // staged conflict — the owner decides those in the profile editor. Fail
        // SAFE: a query that errors still skips the scan, because overwriting a
        // staged value is the unrecoverable outcome and skipping the scan is
        // not. But a read error is NOT evidence that cards exist, so the two
        // outcomes carry different messages — an error must never claim details
        // were prepared when nothing was read.
        //
        // This count is UX ONLY: it avoids a scan whose result would be thrown
        // away. It is a check-then-act and cannot be the correctness boundary —
        // a staff run that began before it can land after it. The guarantee that
        // a staff-staged field is never consumed lives in ONE SERVER-SIDE
        // TRANSACTION, accept_uncontested_owner_suggestions, which locks the
        // candidates and re-checks each field before accepting any of them;
        // scanWebsiteIntoDraft builds the profile patch only from the rows that
        // decision cleared, and reports the rest in skippedForStaff.
        let staffCardCount = 0;
        let staffCardCheckFailed = false;
        try {
          const { count, error } = await supabase
            .from("org_profile_suggestions")
            .select("id", { count: "exact", head: true })
            .eq("org_id", org.id)
            .eq("status", "pending")
            // types regen after 20260903120000 lands
            .not("created_by_staff_user_id" as never, "is", null);
          if (error) {
            // Deploy race: a bundle carrying this filter can reach a browser
            // before migration 20260903120000 adds the column, and Postgres
            // answers 42703 (undefined_column). No column means no staff cards
            // can exist yet, so degrade to the pre-Console behaviour and scan,
            // rather than blocking every wizard until the migration lands.
            if ((error as { code?: string }).code === "42703") staffCardCount = 0;
            else staffCardCheckFailed = true;
          } else {
            staffCardCount = count ?? 0;
          }
        } catch {
          staffCardCheckFailed = true;
        }

        if (staffCardCheckFailed) {
          // Skip the scan (fail safe) without asserting anything about what is
          // waiting in the editor — we did not manage to look.
          setScanned(true);
          toast.message("We couldn't check your website just now", {
            description: "You can import from your website in your story editor.",
          });
        } else if (staffCardCount > 0) {
          setScanned(true);
          toast.message("We've already prepared details from your website", {
            description: "Review them in your story editor after setup.",
          });
        } else {
          setIsScanning(true);
          // Do not overwrite fields the admin filled by hand on this step.
          const skip = new Set<string>();
          if (data.tagline.trim()) skip.add("tagline");
          if (data.description.trim()) skip.add("description");
          try {
            const result = await scanWebsiteIntoDraft(org.id, skip);
            setScanned(true);
            const appliedKeys = Object.keys(result.applied);
            if (!result.ok) {
              // The scan did not complete. The case worth naming is
              // reason === "rpc_missing": the bundle is ahead of the migration
              // carrying the atomic decision, so the scan could not establish
              // that a field is uncontested and deliberately applied NOTHING.
              // Say only that we could not check — never that we found nothing,
              // which would assert a result we never obtained.
              toast.message("We couldn't check your website just now", {
                description: "You can import from your website in your story editor.",
              });
            } else if (appliedKeys.length > 0) {
              // Reseed local state so the next steps show the scanned drafts.
              setData((d) => ({ ...d, ...result.applied }));
              // The scan wrote these itself. Advance the seed too, so the final
              // persist does not re-send a value it never asked the admin about.
              seedRef.current = { ...seedRef.current, ...(result.applied as Partial<typeof emptyData>) };
              void refetchOrg();
              toast.success(
                `Added ${appliedKeys.length} ${appliedKeys.length === 1 ? "detail" : "details"} from your website.`,
              );
            } else if (result.found === 0) {
              toast.message("We couldn't find much on your site — fill the rest in as you go.");
            }
            if (result.skippedForStaff.length > 0) {
              // Staff cards existed for some fields — either already when the
              // scan read them, or arriving mid-scan, which the preflight count
              // above cannot see. Say why those fields were left blank rather
              // than leaving the admin to wonder.
              toast.message("We've already prepared details from your website", {
                description: "Review them in your story editor after setup.",
              });
            }
          } finally {
            setIsScanning(false);
          }
        }
      }
    }
    advance(3);
  };

  const handleNext = async () => {
    if (currentStep === 2) {
      await leaveBrandStep();
    } else if (currentStep < TOTAL_STEPS) {
      advance(currentStep + 1);
    } else {
      // Stay on the final step if the save failed rather than advancing past
      // unsaved input.
      const ok = await persist();
      if (ok) {
        // Read the score this save produced so the completion screen reflects it.
        setCompletionStrength(await refreshStrength());
        setCurrentStep(TOTAL_STEPS + 1); // Completion
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  // The wizard never publishes itself (#1065). It saves its work, closes and
  // hands off to the story editor's own Publish, so the first-publish URL
  // confirmation, the Trust Review and the editor's compare-and-swap all apply
  // unchanged. A failed save keeps the admin here with persist's error.
  const handlePublish = async () => {
    if (!org?.id) return;
    setIsPublishing(true);
    try {
      const ok = await persist();
      if (!ok) return;
      finish();
      // StoryEditorV2Page reads ?publish=1 once and starts its Publish. The
      // older editor (ORG_WORKSPACE_V2 off) has no such trigger, so say where
      // Publish is rather than implying it already happened.
      if (!FEATURE_FLAGS.ORG_WORKSPACE_V2) {
        toast.message("Review your story, then press Publish to go live.");
      }
      navigate("/admin/profile?publish=1");
    } finally {
      setIsPublishing(false);
    }
  };

  // A dismissal — ONLY the X or "Skip for now" (#787) — snoozes the auto-open
  // for a week; the banner keeps nudging and the wizard stays re-openable. The
  // stamp goes through the bookmark writer, so it is not a profile edit: it
  // leaves updated_at and profile_updated_by alone.
  const handleDismiss = () => {
    if (org?.id) {
      void saveOrgOnboardingBookmark(org.id, {
        onboardingDismissedAt: new Date().toISOString(),
      }).then((res) => {
        if (res.status !== "saved") {
          Sentry.captureException(new Error("[org-wizard] dismiss save failed"), {
            tags: { component: "org-onboarding-modal", stage: "dismiss" },
          });
        }
      });
    }
    onClose();
  };

  const finish = () => {
    onComplete();
    onClose();
    setCurrentStep(1);
    setData(emptyData);
  };

  const handlePreview = () => {
    const slug = org?.slug || (org?.name || "").toLowerCase().replace(/\s+/g, "-");
    finish();
    if (slug) navigate(`/organization/${slug}?preview=true`);
  };

  // Below-threshold finish: close the wizard and drop the admin into the profile
  // editor to keep filling it in, rather than pushing an empty profile live.
  const handleFinishInEditor = () => {
    finish();
    navigate("/admin/profile");
  };

  return (
    // Radix reports every close through onOpenChange(false). With the two
    // handlers on DialogContent below, the only close that still reaches it is
    // the X button, which is an explicit dismissal.
    <Dialog
      open={isOpen && !!org}
      onOpenChange={(open) => {
        if (!open) handleDismiss();
      }}
    >
      <DialogContent
        // A column with a pinned header and footer: only the middle scrolls,
        // and only on a viewport too short for the step. bg-card is required
        // under ink, not decorative: the canvas scope turns --background grey.
        className={cn(
          "flex max-h-[92vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:rounded-2xl",
          ink && "theme-ink-canvas bg-card",
        )}
        // A click outside the wizard is too easy to make by accident to count
        // as "stop asking me for a week" (#787). It does nothing.
        onInteractOutside={(e) => e.preventDefault()}
        // Escape still closes — keyboard users keep the standard way out of a
        // dialog — but only for this page view: nothing is written, so it is
        // not a snooze. The gate's once-per-session flag already keeps the
        // wizard from auto-opening again in this tab.
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onClose();
        }}
      >
        <DialogTitle className="sr-only">Tell your organization's story</DialogTitle>
        <DialogDescription className="sr-only">
          A few quick steps to build your public story.
        </DialogDescription>
        {/* Header — step, title and progress, shown on the content steps only */}
        {currentStep <= TOTAL_STEPS && (
          <div className="shrink-0 px-6 pt-6 sm:px-8">
            <p className="eyebrow text-[11px] text-gold-ink">
              Step {currentStep} of {TOTAL_STEPS}
            </p>
            <h2 className="mt-2 pr-6 font-heading text-h3 text-ink-900">{STEP_HEADERS[currentStep].title}</h2>
            <p className="mt-1 max-w-xl text-sm text-ink-500">{STEP_HEADERS[currentStep].subtitle}</p>
            <Progress value={progress} className="mt-5 h-1" />
          </div>
        )}

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 sm:px-8">
          {currentStep === 1 && <OrgOnboardingStep1 />}

          {currentStep === 2 && (
            <OrgOnboardingStep2
              data={{
                logo: data.logo,
                tagline: data.tagline,
                description: data.description,
                websiteUrl: data.websiteUrl,
                scanWebsite: data.scanWebsite,
                type: data.type,
              }}
              onUpdate={(d) => setData({ ...data, ...d })}
            />
          )}

          {currentStep === 3 && (
            <OrgOnboardingStep3
              data={{
                missionStatement: data.missionStatement,
                whatMakesUsDifferent: data.whatMakesUsDifferent,
              }}
              orgId={org?.id}
              onUpdate={(d) => setData({ ...data, ...d })}
            />
          )}

          {currentStep === 4 && (
            <OrgOnboardingStep4
              data={{
                compensationModels: data.compensationModels,
                providesLeads: data.providesLeads,
                trainingFrequency: data.trainingFrequency,
                selectedBenefits: data.selectedBenefits,
              }}
              onUpdate={(d) => setData({ ...data, ...d })}
            />
          )}

          {currentStep === 5 && (
            <OrgOnboardingStep5
              data={{
                serviceTags: data.serviceTags,
                marketsServed: data.marketsServed,
                locations: data.locations,
              }}
              onUpdate={(d) => setData({ ...data, ...d })}
            />
          )}

          {currentStep === 6 && (
            <OrgOnboardingStep6 orgId={org?.id} />
          )}

          {currentStep === TOTAL_STEPS + 1 && (
            <OrgOnboardingCompletion
              orgName={org?.name || "Your organization"}
              strength={completionStrength}
              isPublished={publishedLocal}
              isPublishing={isPublishing}
              onPreview={handlePreview}
              onPublish={handlePublish}
              onFinishInEditor={handleFinishInEditor}
              onDone={finish}
            />
          )}
        </div>

        {/* Footer — pinned, so the way forward never scrolls out of reach.
            "Skip for now" is the low-weight dismissal: it snoozes the auto-open
            without finishing, so the banner keeps nudging and the wizard stays
            re-openable. Hidden on the completion step. */}
        {currentStep <= TOTAL_STEPS && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-6 py-4 sm:px-8">
            <button
              type="button"
              onClick={handleDismiss}
              disabled={isSaving}
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors disabled:opacity-50"
            >
              Skip for now
            </button>
            <div className="flex items-center gap-3">
              {currentStep > 1 && (
                <Button variant="outline" onClick={handleBack} disabled={isScanning || isSaving}>
                  Back
                </Button>
              )}
              <Button className="min-w-[9rem] gap-2" onClick={handleNext} disabled={isScanning || isSaving}>
                {isScanning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Scanning your website…
                  </>
                ) : isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    {currentStep === 1 ? "Get started" : "Continue"}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrgOnboardingModal;
